import React from "react";

import { DaperStorySite, DaperStoryType } from "@data/daper-client";
import { IntercomTracking } from "@interface/IntercomSlot";
import type {
  IntercomComponentProps,
  IntercomInterface,
} from "@interface/types";
import { sortByPublishedAt } from "@util/sortChronological";
import stripMarkupFromText from "@util/stripMarkupFromText";
import { TDateISO } from "types/date";

import * as FilterableList from "../../components/filterable-list/index";
import * as ListItem from "../../components/list-item/index";
import { Skeleton } from "../../components/skeleton/index";

export type LatestStoriesFilterableListProps = {
  hideFilters?: boolean;
  title?: string;
  link?: {
    text: string;
    url: string;
  };
  intercom: IntercomInterface;
} & IntercomComponentProps;

const ALL_FILTERS = [
  DaperStorySite.markets,
  DaperStorySite.economics,
  DaperStorySite.technology,
  DaperStorySite.bpol,
  DaperStorySite.crypto,
  DaperStorySite.green,
];

export function LatestStoriesFilterableList(props: LatestStoriesFilterableListProps) {
  const { hideFilters = false, title = "Latest News", link } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const getLatestStoriesChronological = async (
    params?
  ): Promise<ListItem.Props[]> => {
    setIsLoading(true);

    const stories = await props?.intercom.getStories({
      ...params,
      limit: 5,
      type: [
        DaperStoryType.article,
        DaperStoryType.feature,
        DaperStoryType.interactive,
      ],
      page: 1,
      sortOrder: "desc",
      sortBy: "groupPublishedAt",
    });

    setIsLoading(false);

    return stories.sort(sortByPublishedAt).map((story) => ({
      text: stripMarkupFromText(
        story.headlines.markup?.web || story.headlines.web
      ),
      timestamp: story.publishedAt as TDateISO,
      url: story.url,
    }));
  };

  const filterFunctions = {
    "All categories": () =>
      getLatestStoriesChronological({
        primarySites: ALL_FILTERS,
      }),
    Markets: () =>
      getLatestStoriesChronological({
        primarySites: [DaperStorySite.markets],
      }),
    Economics: () =>
      getLatestStoriesChronological({
        primarySites: [DaperStorySite.economics],
      }),
    Technology: () =>
      getLatestStoriesChronological({
        primarySites: [DaperStorySite.technology],
      }),
    Politics: () =>
      getLatestStoriesChronological({
        primarySites: [DaperStorySite.bpol],
      }),
    Crypto: () =>
      getLatestStoriesChronological({ primarySites: [DaperStorySite.crypto] }),
    AI: () =>
      getLatestStoriesChronological({
        "contentTags.Topic": ["artificial-intelligence"],
        primarySites: ALL_FILTERS,
      }),
    Green: () =>
      getLatestStoriesChronological({ primarySites: [DaperStorySite.green] }),
  };

  const ListItemWithSkeleton = (componentProps: ListItem.Props) => (
    <Skeleton isLoading={isLoading}>
      <ListItem.WithRedTimestampOnTop {...componentProps} />
    </Skeleton>
  );

  return (
    <IntercomTracking componentName="LatestStoriesFilterableList">
      <FilterableList.WithRedTitle
        hideFilters={hideFilters}
        title={{
          text: title,
          url: link?.url,
        }}
        filterFunctions={filterFunctions}
        IterableComponent={(componentProps: ListItem.Props) => (
          <div
            style={{
              borderBottom: "1px solid #efefef",
              paddingBottom: "12px",
            }}
          >
            <ListItemWithSkeleton {...componentProps} />
          </div>
        )}
        numberOfRows={5}
        link={link}
      />
    </IntercomTracking>
  );
}
